import axios from 'axios';

const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

const getCorrectMimeType = (file) => {
  const ext = file.name.split('.').pop().toLowerCase();
  const mimeMap = {
    'mp3': 'audio/mp3', 'wav': 'audio/wav', 'ogg': 'audio/ogg',
    'flac': 'audio/flac', 'm4a': 'audio/mp4', 'aac': 'audio/aac',
    'webm': 'audio/webm', 'mp4': 'audio/mp4'
  };
  if (file.type && file.type.startsWith('audio/') && file.type !== 'audio/x-m4a') {
    return file.type;
  }
  return mimeMap[ext] || 'audio/mp3';
};

const convertAudioToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const analyzeAudioWithGemini = async (audioFile) => {
  const apiKey = localStorage.getItem('openrouter_api_key') || import.meta.env.VITE_OPENROUTER_API_KEY;
  const model = localStorage.getItem('selected_model') || "google/gemini-1.5-flash";

  if (!apiKey) {
    throw new Error('OpenRouter API key not found. Please click Settings to configure your API key.');
  }

  try {
    const fileSizeMB = audioFile.size / (1024 * 1024);
    if (fileSizeMB > 12) {
      throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Limit is ~10-12MB for browser uploads due to API payload restrictions.`);
    }

    const audioBase64 = await convertAudioToBase64(audioFile);
    const mimeType = getCorrectMimeType(audioFile);
    console.log(`Analyzing file: ${audioFile.name} | Detected Mime: ${mimeType} | Size: ${fileSizeMB.toFixed(2)}MB`);

    const prompt = `
CRITICAL INSTRUCTION: You are an expert audio analysis AI. I have attached an audio file to this message.
If you CANNOT hear/process the audio, or if it seems silent/corrupted, DO NOT GUESS. Instead, return a JSON with "error": "No audio detected".

Analyze the provided audio track (Filename: "${audioFile.name}") and provide a comprehensive analysis.

1. AUDIO ANALYSIS:
   - Identify the TRUE genre and style.
   - Main themes and mood.

2. SEO-OPTIMIZED YOUTUBE TITLES — Generate EXACTLY 5 DIFFERENT title versions for this specific song:
   - Each title must be UNIQUE in style and angle.
   - CRITICAL: Avoid generic titles like "Violin Instrumental" or "Techno Track".
   - Use MOOD-BASED or SCENARIO-BASED patterns.
   - Cover these 5 angles (one each):
     * Version 1 — Scenario/Use-case (e.g., "Music to Study at 2AM Without Losing Your Mind")
     * Version 2 — Emotional/Mood (e.g., "This Song Feels Like Watching Rain From a Window")
     * Version 3 — Duration/Session (e.g., "1 Hour of Deep Focus Cello for Overthinkers")
     * Version 4 — Identity/Listener (e.g., "Late Night Phonk for People Who Drive Alone")
     * Version 5 — Cinematic/Poetic (e.g., "Soaring Strings for the Ones Who Never Give Up")
   - Max 100 characters each.

3. COMPREHENSIVE YOUTUBE DESCRIPTION:
   - 3-4 paragraphs.
   - Include high-traffic hashtags (#music #chill etc.) ONLY here.

4. YOUTUBE TAGS (15-20 keywords):
   - Plain keywords, no hashtags.

5. THUMBNAIL GENERATION PROMPT:
   - Visual Style: Simple, Aesthetic, High Quality.
   - Lighting: Warm lights, dim/cozy lighting, or cinematic lighting.
   - Composition: Instruments or subject in foreground/close-up.
   - Text Requirement: Mention that text should be "Short, readable, and minimal".
   - The prompt should describe the image to be generated by an AI image generator.

6. SUNO AI V5 STYLE PROMPT (IMPORTANT):
   - CRITICAL LENGTH CONSTRAINT: The prompt MUST be between 420 and 480 characters.
   - You MUST pad the prompt with relevant descriptive tags, atmospheric details, and technical terms to reach this length.
   - Format: [Genre],[Sub-genre],[Tempo],[Key Instruments],[Vocal Style],[Detailed Mood Tags],[Atmosphere],[Production Quality Tags]

Format your response as a JSON object with the following structure:
{
  "analysis": {"themes": "...","mood": "...","sonicElements": "..."},
  "youtubeContent": {
    "titles": [
      {"version": 1, "angle": "Scenario/Use-case", "title": "..."},
      {"version": 2, "angle": "Emotional/Mood", "title": "..."},
      {"version": 3, "angle": "Duration/Session", "title": "..."},
      {"version": 4, "angle": "Identity/Listener", "title": "..."},
      {"version": 5, "angle": "Cinematic/Poetic", "title": "..."}
    ],
    "description": "...",
    "tags": ["tag1","tag2",...]
  },
  "thumbnailPrompt": "...",
  "sunoPrompt": "..."
}
`;

    const dataURI = `data:${mimeType};base64,${audioBase64}`;

    const response = await axios.post(
      OPENROUTER_API_URL,
      {
        model: model,
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: prompt },
              { type: "image_url", image_url: { url: dataURI } }
            ]
          }
        ],
        max_tokens: 4000,
        temperature: 0.3
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Music Analysis App'
        }
      }
    );

    const content = response.data.choices[0].message.content;

    if (content.includes("No audio detected") || content.includes("ERROR_NO_AUDIO")) {
      throw new Error("The AI could not hear the audio. The file format might be unsupported or the file is corrupted.");
    }

    let jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('Invalid response format from AI');

    const results = JSON.parse(jsonMatch[0]);

    if (results.error) throw new Error(`AI Error: ${results.error}`);
    if (!results.analysis || !results.youtubeContent || !results.thumbnailPrompt) {
      throw new Error('Incomplete analysis results');
    }

    // Normalize: support both old "title" string and new "titles" array
    if (!results.youtubeContent.titles && results.youtubeContent.title) {
      results.youtubeContent.titles = [
        { version: 1, angle: "Main Title", title: results.youtubeContent.title }
      ];
    }

    if (results.youtubeContent.tags && Array.isArray(results.youtubeContent.tags)) {
      results.youtubeContent.tags = results.youtubeContent.tags.map(tag => tag.replace(/^#/, '').trim());
    }

    return results;
  } catch (error) {
    console.error('Error analyzing audio:', error);
    if (error.response) {
      if (error.response.status === 413) {
        throw new Error('File is too large for the API. Please use a file smaller than 10MB.');
      }
      throw new Error(`API Error: ${error.response.data.error?.message || error.message}`);
    } else if (error.message.includes('JSON')) {
      throw new Error('Failed to parse AI response. Please try again.');
    } else {
      throw new Error(error.message || 'Failed to analyze audio.');
    }
  }
};